import { Pill } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
// eslint-disable-next-line no-restricted-imports
import { Box, Stack, Tab, Tabs } from "@mui/material";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useCreateOrGetPlacementCandidate } from "../../PlacementCandidate/api/useCreateOrGetPlacementCandidate";
import { useGetEasySchedulingPlacements } from "../api/useGetEasySchedulingPlacements";

interface JobsApplicationsTabsProps {
  showApplications: boolean;
  onChange: (showApplications: boolean) => void;
}

export function JobsApplicationsTabs({ showApplications, onChange }: JobsApplicationsTabsProps) {
  const worker = useDefinedWorker();

  const { data: placementCandidate } = useCreateOrGetPlacementCandidate({
    workerId: worker.userId,
  });

  // Fetch easy scheduling placements - count from first page (200 items)
  const { data: easySchedulingData } = useGetEasySchedulingPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate?.id ?? "",
      shouldLogEvent: false,
    },
    {
      enabled: isDefined(worker.userId) && isDefined(placementCandidate?.id),
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  const easySchedulingCount = easySchedulingData?.pages[0]?.data.length ?? 0;

  return (
    <Tabs
      value={showApplications ? "applications" : "jobs"}
      variant="fullWidth"
      sx={{
        width: "100%",
        border: "none",
        borderRadius: 0,
        borderBottom: (theme) => `3px solid ${theme.palette.background.secondary}`,
        "& .MuiTabs-indicator": {
          backgroundColor: (theme) => theme.palette.text.primary,
          height: 3,
          bottom: 0,
          top: "auto",
        },
        "& .MuiTab-root": {
          minHeight: 45,
          fontWeight: (theme) => theme.typography.fontWeightMedium,
          color: (theme) => theme.palette.text.disabled,
          backgroundColor: "transparent",
        },
        "& .MuiTab-root.Mui-selected": {
          color: (theme) => theme.palette.text.primary,
        },
      }}
      onChange={(_, newValue) => {
        onChange(newValue === "applications");
      }}
    >
      <Tab value="jobs" label="Open Staff Jobs" />
      <Tab
        value="applications"
        label={
          <Stack alignItems="center" direction="row" spacing={2}>
            <Box>My Jobs</Box>
            {easySchedulingCount > 0 ? (
              <Pill
                label={easySchedulingCount}
                size="small"
                sx={{
                  backgroundColor: (theme) => theme.palette.error.main,
                  color: (theme) => theme.palette.error.contrastText,
                  padding: 0,
                  minHeight: 20,
                  minWidth: 20,
                  height: "max-content",
                  borderRadius: 10,
                }}
              />
            ) : undefined}
          </Stack>
        }
      />
    </Tabs>
  );
}
